'use client'
import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useCurrency } from '@/lib/currency'
import { useAuth } from '@/context/AuthContext'

const PLANS = [
  { id: 'free', name: 'Free',  monthlyEur: 0,    yearlyMonthlyEur: 0,    yearlyTotalEur: null, cta: 'Browse free', href: '/deals',   variant: 'ghost',
    highlights: ['Limited deals', '1 home airport', 'European deals only', 'Daily Telegram top-5'] },
  { id: 'plus', name: 'Plus',  monthlyEur: 4.99, yearlyMonthlyEur: 2.92, yearlyTotalEur: 35,   cta: 'Get Plus',    href: '/pricing', variant: 'primary', popular: true, savePct: 42,
    highlights: ['All deals, unlimited', '5 home airports', 'Daily email + Telegram digest', 'Price history & filters'] },
  { id: 'pro',  name: 'Pro',   monthlyEur: 9.99, yearlyMonthlyEur: 4.08, yearlyTotalEur: 49,   cta: 'Get Pro',     href: '/pricing', variant: 'dark', savePct: 59,
    highlights: ['Everything in Plus', '15 home airports', 'Intercontinental deals', 'Real-time Telegram alerts'] },
]

function fmtEur(amount) {
  if (amount === 0) return '€0'
  return Number.isInteger(amount) ? `€${amount}` : `€${amount.toFixed(2).replace('.', ',')}`
}

export default function HomePricingCards() {
  const [yearly, setYearly] = useState(true)
  const { formatPlanPrice } = useCurrency()
  const { currentUser } = useAuth()
  const [stripeCustomerId, setStripeCustomerId] = useState(null)
  const [prorations, setProrations] = useState({})

  useEffect(() => {
    if (!currentUser) return
    import('@/lib/userProfile').then(({ getUserProfile }) =>
      getUserProfile(currentUser.uid).then(p => setStripeCustomerId(p?.stripeCustomerId ?? null)).catch(() => {})
    )
  }, [currentUser])

  useEffect(() => {
    if (!stripeCustomerId) return
    const paid = ['plus', 'pro']
    const results = {}
    Promise.all(paid.map(async planId => {
      const planKey = yearly ? `${planId}_yearly` : `${planId}_monthly`
      try {
        const res = await fetch('/api/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'preview', customerId: stripeCustomerId, plan: planKey }),
        })
        const data = await res.json()
        if (data.amountDue != null) results[planId] = data
      } catch {}
    })).then(() => setProrations(results))
  }, [stripeCustomerId, yearly])

  return (
    <div>
      {/* Billing toggle */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1">
          <button
            onClick={() => setYearly(false)}
            className={`text-sm font-medium px-4 py-1.5 rounded-full transition-colors ${
              !yearly ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setYearly(true)}
            className={`flex items-center gap-1.5 text-sm font-medium px-4 py-1.5 rounded-full transition-colors ${
              yearly ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Yearly
            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${yearly ? 'bg-blue-500 text-white' : 'bg-green-100 text-green-700'}`}>
              −42%
            </span>
          </button>
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {PLANS.map(plan => {
          const displayMonthly = yearly ? plan.yearlyMonthlyEur : plan.monthlyEur
          const hint = formatPlanPrice(displayMonthly)
          const proration = prorations[plan.id]
          return (
            <div key={plan.name} className={`rounded-2xl border-2 bg-white p-5 flex flex-col ${plan.popular ? 'border-blue-600 shadow-lg shadow-blue-100' : 'border-gray-200'}`}>
              {plan.popular
                ? <div className="text-center mb-2"><span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Most Popular</span></div>
                : <div className="mb-2 h-[22px]" />
              }
              <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${plan.popular ? 'text-blue-600' : 'text-gray-400'}`}>{plan.name}</p>
              <div className="flex items-baseline gap-1 mb-0.5">
                <span className="text-3xl font-bold text-gray-900">{fmtEur(displayMonthly)}</span>
                <span className="text-sm text-gray-400">/mo</span>
                {hint && displayMonthly > 0 && <span className="ml-1 text-sm font-normal text-gray-400">({hint})</span>}
              </div>
              {plan.id !== 'free' && proration && proration.amountDue > 0 ? (
                <p className="text-xs font-semibold text-blue-700 mb-1 min-h-[1.25rem]">
                  Today: €{(proration.amountDue / 100).toFixed(2).replace('.', ',')} (prorated)
                </p>
              ) : plan.yearlyTotalEur ? (
                <p className="text-xs text-gray-400 mb-1 min-h-[1.25rem]">
                  {yearly
                    ? <span>{fmtEur(plan.yearlyTotalEur)} / year{plan.savePct && <span className="ml-1.5 inline-block bg-green-100 text-green-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">save {plan.savePct}%</span>}</span>
                    : <span>or {fmtEur(plan.yearlyTotalEur)} / year</span>
                  }
                </p>
              ) : (
                <div className="min-h-[1.25rem] mb-1" />
              )}
              <ul className="space-y-1.5 mb-5 flex-1 mt-2">
                {plan.highlights.map(h => (
                  <li key={h} className="flex items-start gap-1.5">
                    <svg className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    <span className="text-xs text-gray-600 leading-snug">{h}</span>
                  </li>
                ))}
              </ul>
              <Link
                href={plan.href}
                className={`mt-auto block text-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                  plan.variant === 'primary' ? 'bg-blue-600 text-white hover:bg-blue-700' :
                  plan.variant === 'dark'    ? 'bg-gray-900 text-white hover:bg-gray-800' :
                                               'border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          )
        })}
      </div>
    </div>
  )
}
